import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MatchCard from '@/components/base/MatchCard';
import ArticleCard from '@/components/base/ArticleCard';
import { SeoMeta } from '@/components/base/SeoHead';
import { siteInfo, categories, matches } from '@/mocks/matches';
import { articles } from '@/mocks/articles';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteInfo.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: siteInfo.url,
  logo: `${siteInfo.url}/logo.png`,
  description: siteInfo.description,
  email: siteInfo.email,
  telephone: siteInfo.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: siteInfo.address,
    addressLocality: '北京',
    addressCountry: 'CN',
  },
};

export default function HomePage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const hotMatches = matches.filter((m) => m.isHot).slice(0, 4);
  const latestArticles = articles.slice(0, 3);
  const allMatches = matches.slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <SeoMeta
        title={`${siteInfo.name} - ${siteInfo.tagline}`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
        ogImage="https://readdy.ai/api/search-image?query=Dynamic%20sports%20stadium%20with%20dramatic%20lighting%20and%20energy%2C%20professional%20sports%20branding%2C%20competitive%20spirit%2C%20cinematic%20wide%20shot%2C%20green%20field%20and%20warm%20golden%20accents&width=1200&height=630&seq=og-home&orientation=landscape"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      <Navbar />
      <HeroSection />

      <main>
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                热门赛事分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">选择您感兴趣的运动项目，开启竞猜之旅</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-100 rounded-lg p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-200 no-underline flex items-start gap-4"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500/20 transition-colors">
                    <i className={`${cat.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      {cat.description.length > 60 ? cat.description.slice(0, 60) + '...' : cat.description}
                    </p>
                    <span className="text-xs text-primary-500 font-medium">
                      {cat.matchCount}场赛事可竞猜
                      <i className="ri-arrow-right-line ml-1 text-xs"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门赛事推荐
                </h2>
                <p className="text-sm text-foreground-600">精选高关注度赛事，实时赔率更新</p>
              </div>
              <Link
                to="/category/football"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium no-underline flex items-center gap-1 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {hotMatches.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                全部赛事一览
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">覆盖全球顶级联赛，实时赔率一目了然</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allMatches.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/category/football"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-primary-500 text-primary-500 font-medium rounded-lg hover:bg-primary-50 transition-colors text-sm no-underline whitespace-nowrap"
              >
                浏览更多赛事 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新赛事资讯
                </h2>
                <p className="text-sm text-foreground-600">专业分析师为您深度解读赛事走势</p>
              </div>
              <Link
                to="/category/football"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium no-underline flex items-center gap-1 whitespace-nowrap"
              >
                更多资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              准备好开始您的竞猜之旅了吗？
            </h2>
            <p className="text-background-50/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              加入500万+用户的真人体育平台，体验最专业的赛事竞猜服务
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/football"
                className="px-8 py-3 bg-background-50 text-primary-500 font-semibold rounded-lg hover:bg-background-100 transition-all duration-200 text-sm no-underline whitespace-nowrap"
              >
                立即开始竞猜
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-primary-600 text-background-50 font-semibold rounded-lg hover:bg-primary-700 transition-all duration-200 text-sm no-underline whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}